/**
 * Ratio and KPI calculation (ADR-0007).
 *
 * Two rules, and they are not negotiable per-metric:
 *
 *   1. Every ratio is calculated, never a raw input. Nothing here accepts a
 *      pre-computed value from a client payload.
 *   2. Every ratio declares what it means when its denominator is zero. There is
 *      no default. Calling the helper without saying forces the caller to decide,
 *      which is the point.
 *
 * Phase 1 establishes the discipline; App 1's metrics (closure rate, PQI, variance)
 * are built on it in phase 2.
 */
/**
 * Percentage of numerator over denominator, 0–100, with an explicit answer when
 * the denominator is zero.
 *
 * @param rule what this specific metric means when the denominator is zero
 */
export function percentage(numerator, denominator, rule, options = {}) {
    const decimals = options.decimals ?? 1;
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
        throw new TypeError('percentage() requires finite numbers; got a NaN or Infinity input.');
    }
    if (numerator < 0 || denominator < 0) {
        throw new RangeError('percentage() requires non-negative counts.');
    }
    if (denominator === 0) {
        switch (rule.kind) {
            case 'complete':
                return {
                    value: 100,
                    fromZeroDenominatorRule: true,
                    explanation: 'Nothing was outstanding in this period, so nothing was left unclosed.',
                };
            case 'not_applicable':
                return {
                    value: null,
                    fromZeroDenominatorRule: true,
                    explanation: 'No records in this period, so this metric does not apply.',
                };
            case 'value':
                return {
                    value: rule.value,
                    fromZeroDenominatorRule: true,
                    explanation: `No records in this period; this metric is defined as ${rule.value} when empty.`,
                };
        }
    }
    const raw = (numerator / denominator) * 100;
    const factor = 10 ** decimals;
    return {
        value: Math.round(raw * factor) / factor,
        fromZeroDenominatorRule: false,
        explanation: `${numerator} of ${denominator}.`,
    };
}
/**
 * A plain average, with the same discipline `percentage()` applies.
 *
 * Several App 1 fields are averages rather than percentages — attendance per
 * toolbox talk, days per document review, a satisfaction rating out of five.
 * Forcing those through `percentage()` would multiply them by 100 and produce a
 * number that looks like a percentage and is not one.
 *
 * @param rule what this specific metric means when there is nothing to average
 */
export function mean(total, count, rule, options = {}) {
    const decimals = options.decimals ?? 2;
    if (!Number.isFinite(total) || !Number.isFinite(count)) {
        throw new TypeError('mean() requires finite numbers; got a NaN or Infinity input.');
    }
    if (count < 0) {
        throw new RangeError('mean() requires a non-negative count.');
    }
    if (count === 0) {
        switch (rule.kind) {
            case 'complete':
                return {
                    value: 100,
                    fromZeroDenominatorRule: true,
                    explanation: 'Nothing was outstanding in this period.',
                };
            case 'not_applicable':
                return {
                    value: null,
                    fromZeroDenominatorRule: true,
                    explanation: 'Nothing was recorded in this period, so there is no average to report.',
                };
            case 'value':
                return {
                    value: rule.value,
                    fromZeroDenominatorRule: true,
                    explanation: `Nothing was recorded in this period; this metric is defined as ${rule.value} when empty.`,
                };
        }
    }
    const factor = 10 ** decimals;
    return {
        value: Math.round((total / count) * factor) / factor,
        fromZeroDenominatorRule: false,
        explanation: `${total} across ${count}.`,
    };
}
/**
 * Closure Rate — the worked example from the source BRD (spec §5.1).
 *
 * Zero-denominator rule: **100% when both Issued and Closed are 0** for an active
 * project in a period. Nothing was outstanding, so nothing was left unclosed. This
 * is the rule the BRD states; it is not an assumption.
 */
export function closureRate(issued, closed) {
    return percentage(closed, issued, { kind: 'complete' });
}
/**
 * Month-over-month variance in percentage points, or null when there is no prior
 * period to compare against.
 *
 * A first-ever reporting period has no variance — it is not a variance of zero,
 * and showing 0 would read as "no change" when the truth is "nothing to compare".
 */
export function variancePoints(current, previous) {
    if (current === null || previous === null) {
        return {
            value: null,
            explanation: 'No comparable prior period, so variance does not apply.',
        };
    }
    return {
        value: Math.round((current - previous) * 10) / 10,
        explanation: `${current} this period against ${previous} last period.`,
    };
}
//# sourceMappingURL=ratio.js.map